import { useMemo } from 'react';
import { Category, Subcategory } from '../types/category';
import { ProductItem } from '../types/productTypes';

/**
 * A hook which returns a list of product items to display in productTable
 * Creates ProductItem objects by matching categoryId between Category & Subcategory objects
 * @param selectedProductGroup - id of selected productGroup
 * @param productGroupList - list of all product groups
 * @param categoryList - list of all categories for the product group
 * @param subcategoryList - list of subcategories for product group
 *
 * Used to provide data to display in main table by matching subcategory to parent category
 * Denormalized product taxonomy data into a simple list of ProductItems to be used by productTable
 */

interface ProductGroup {
  id: string;
  name: string;
}

const useProductItems = (
  selectedProductGroup: string,
  productGroupList: ProductGroup[],
  categoryList?: Category[],
  subcategoryList?: Subcategory[],
) =>
  useMemo(() => {
    if (!selectedProductGroup || !subcategoryList || !categoryList) return [];

    const productGroupName = productGroupList.find((pg) => pg.id === selectedProductGroup)?.name || '';

    const items: ProductItem[] = [];

    subcategoryList.forEach((subcategory) => {
      const category = categoryList.find((cat) => cat.categoryId === subcategory.categoryId);

      if (category) {
        items.push({
          id: subcategory.subcategoryId,
          productGroup: productGroupName,
          category: category.categoryName,
          extraDimension:
            category.extraDimensions && category.extraDimensions.length > 0 ? category.extraDimensions[0] : '',
          subcategory: subcategory.subcategoryName,
        });
      }
    });

    return items;
  }, [selectedProductGroup, productGroupList, categoryList, subcategoryList]);

export default useProductItems;
